import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { GridApi, GridOptions, RowNode } from 'ag-grid-community';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { CxoneWallboardsService } from './cxone-wallboards.service';
import { WallboardModule } from '../../wallboards/wallboards.model';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { CXOneWallboardContextMenuCellRendererComponent } from './context-menu-cell-renderer/context-menu-cell-renderer.component';
import { CxoneTruncateTextCellRendererComponent } from '../../reusables/ag-grid-components/cxone-truncate-text-cell-renderer/cxone-truncate-text-cell-renderer.component';
import { CxoneColumnHeaderRendererComponent } from '../admin/cxone-manage-metrics/ag-grid-components/cxone-column-header-renderer/cxone-column-header-renderer.component';
import { MessageModalComponent } from '@niceltd/cxone-components/modal';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { CustomNoRowsOverlayComponent } from '../admin/shared/custom-no-rows-overlay/custom-no-rows-overlay.component';
import moment from 'moment';

@Component({
    selector: 'cv-cxone-wallboards',
    templateUrl: './cxone-wallboards.component.html',
    styleUrls: ['./cxone-wallboards.component.scss'],
    providers: [DialogService]
})

export class CxoneWallboardsComponent implements OnInit {
    @ViewChild('dialogRef')
    dialogRef!: TemplateRef<any>;
    public gridOptions: GridOptions = null;
    public localization: Map<string, string>;
    public searchValue: string = "";
    public curWallboardCount: number = 0;

    public isLoading: boolean = false;
    public wallboards: any[] = [];
    public folders: string[] = [];
    public slideModules: WallboardModule[] = [];
    public domain = "";
    public isBpo = false;

    public settingsModalOpen = false;
    private cancelMessageModalRef: DynamicDialogRef = null;
    public gridApi: GridApi = null;

    constructor(
        private cvLocalizationFactory: CVLocalizationService,
        private agGridHelperService: AgGridHelperService,
        public wallboardsService: CxoneWallboardsService,
        private _dialogService: DialogService,
    ) { }

    async ngOnInit() {
        this.localization = await this.cvLocalizationFactory.GetSection("Wallboard_Management");

        await this.loadWallboards();

        this.setupAgGrid();
    }

    private async loadWallboards(): Promise<void> {
        try {
            this.isLoading = true;
            const returnObj = await this.wallboardsService.loadWallboards();

            this.wallboards = returnObj.Wallboards;
            this.folders = returnObj.Folders;
            this.slideModules = returnObj.Wallboard_Modules;
            this.domain = returnObj.Domain;
            this.isBpo = returnObj.IsBpo;
        }
        catch (error) {
            console.log(error);
        }
        finally {
            this.isLoading = false;
        }
    }

    /**
     * Deletes the wallboard stored in this.confirmDeleteWallboard
     */
    public async deleteWallboard(): Promise<void> {
        const dialogData = {
            title: this.getLocalizationString('Delete', "Delete"),
            type: 'user-warning', //MessageTypes from cxone-components/modal
            message: this.getLocalizationString('Confirm_Delete', "Are you sure you want to delete this wallboard?"),
            closeButtonText: this.getLocalizationString('Cancel_Button', "Cancel"),
            primaryButtonText: this.getLocalizationString('Toggle_Yes', "Yes")
        };

        this.cancelMessageModalRef = this._dialogService.open(MessageModalComponent, {
            width: '420px',
            height: '184px',
            data: dialogData,
            styleClass: "cx-one-ui"
        });
    }

    /**
     * Applies filter to the main AG Grid.
     */
    public applyFilter() {
        if (this.gridApi)
            this.curWallboardCount = 0;

        this.gridApi.onFilterChanged();
    }

    public setupAgGrid() {
        this.gridOptions = this.agGridHelperService.createGridOptions({
            columnTypes: {
                simpleContextMenu: {
                    cellRendererSelector: (param) => {
                        if (param.data == null)
                            return null;
                        else
                            return { component: 'contextMenuCellRenderer' };
                    }
                },
            },
            columnDefs: [
                {
                    field: "WallboardName", 
                    colId: "WallboardName",
                    headerName: this.localization != null ? this.localization.get("Name").toUpperCase() : "Name",
                    cellRenderer: 'cxoneTruncateTextCellRendererComponent',
                    suppressSizeToFit: true,
                    valueFormatter: (params) => {
                        if (params.value != null)
                            return params.value.toString();
                        else
                            return "";
                    },
                    width: 200,
                    flex: 1,
                    lockPinned: true,
                    resizable: true,
                    sortable: true
                },
                {
                    field: "WallboardGroup",
                    colId: "WallboardGroup",
                    headerName: this.localization != null ? this.localization.get("Category").toUpperCase() : "Category",
                    cellRenderer: 'cxoneTruncateTextCellRendererComponent', 
                    suppressSizeToFit: true,
                    resizable: true,
                    sortable: true,
                    width: 200,
                    flex: 1,
                    lockPinned: true,
                    valueFormatter: (params) => {
                        if (params.value != null)
                            return params.value.toString();
                        else
                            return "";
                    },
                },
                {
                    field: "WallboardDescription",
                    colId: "WallboardDescription",
                    headerName: this.localization != null ? this.localization.get("Description_Header").toUpperCase() : "Description",
                    cellRenderer: 'cxoneTruncateTextCellRendererComponent',
                    suppressSizeToFit: true,
                    resizable: true,
                    sortable: true,
                    width: 200,
                    flex: 1,
                    lockPinned: true,
                    valueFormatter: (params) => {
                        if (params.value != null)
                            return params.value.toString();
                        else
                            return "";
                    },
                },
                {
                    field: "CreatedDate",
                    colId: "CreatedDate",
                    headerName: this.localization != null ? this.localization.get("Date_Created").toUpperCase() : "Date Created",
                    cellRenderer: 'cxoneTruncateTextCellRendererComponent',
                    suppressSizeToFit: true,
                    resizable: true,
                    sortable: true,
                    sort: "desc",
                    sortIndex: 1,
                    width: 200,
                    flex: 1,
                    lockPinned: true,
                    valueGetter: (params) => {
                        if (params.data == null)
                            return null;
                        else
                            return params.data?.CreatedDate ? moment(params.data?.CreatedDate).format('MMM D, YYYY h:mm:ss A') : 'NA';
                    },
                    comparator: (dateA, dateB, nodeA: RowNode, nodeB: RowNode, reverse: boolean) => {
                        if (!nodeA.data.CreatedDate && !nodeB.data.CreatedDate)
                            return 0;
                        else if (!nodeA.data.CreatedDate && nodeB.data.CreatedDate)
                            return -1;
                        else if (nodeA.data.CreatedDate && !nodeB.data.CreatedDate)
                            return 1;
                        return new Date(nodeA.data.CreatedDate).compareTo(new Date(nodeB.data.CreatedDate));
                    },
                },
                {
                    field: "LastViewed",
                    colId: "LastViewed",
                    headerName: this.localization != null ? this.localization.get("Last_Viewed").toUpperCase() : "Last Viewed",
                    cellRenderer: 'cxoneTruncateTextCellRendererComponent',
                    suppressSizeToFit: true,
                    resizable: true,
                    sortable: true,
                    width: 200,
                    flex: 1,
                    lockPinned: true,
                    valueGetter: (params) => {
                        if (params.data == null)
                            return null;
                        else
                            return params.data?.LastViewed ? moment(params.data?.LastViewed).format('MMM D, YYYY h:mm:ss A') : 'NA';
                    },
                    comparator: (dateA, dateB, nodeA: RowNode, nodeB: RowNode, reverse: boolean) => {
                        if (!nodeA.data.LastViewed && !nodeB.data.LastViewed)
                            return 0;
                        else if (!nodeA.data.LastViewed && nodeB.data.LastViewed)
                            return -1;
                        else if (nodeA.data.LastViewed && !nodeB.data.LastViewed)
                            return 1;
                        return new Date(nodeA.data.LastViewed).compareTo(new Date(nodeB.data.LastViewed));
                    },
                },
                {
                    colId: "contextMenu",
                    type: "simpleContextMenu",
                    width: 48,
                    suppressSizeToFit: true,
                    suppressMovable: true,
                    lockPosition: "right",
                    cellStyle: { "text-overflow": "unset" },
                    resizable: false,
                    sortable: false
                }
            ],
            getContextMenuItems: params => {
                return null;
            },
            defaultColDef: {
                sortable: false,
                //cellClass: "d-flex align-items-center",
                menuTabs: [],
                resizable: false,
                icons: {
                    sortAscending: '<div class="sortIcons"><svg class="icon-10 fill-cxoneBlue"><use href="../App_Themes/CXone/icons/arrow-up.svg#arrow-up"></use></svg><svg class="icon-10 fill-sort"><use href="../App_Themes/CXone/icons/arrow-down.svg#arrow-down"></use></svg></div>',
                    sortDescending: '<div class="sortIcons"><svg class="icon-10 fill-sort"><use href="../App_Themes/CXone/icons/arrow-up.svg#arrow-up"></use></svg><svg class="icon-10 fill-cxoneBlue"><use href="../App_Themes/CXone/icons/arrow-down.svg#arrow-down"></use></svg></div>',
                    filter: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/filter.svg#filter"></use></svg>',
                    menu: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/widget-menu.svg#widget-menu"></use></svg>',
                    columns: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/chart.svg#chart"></use></svg>',
                }
            },
            onGridReady: params => {
                this.gridApi = params.api;
                params.api.setGridOption('rowData', this.wallboards);
            },
            onCellClicked: event => {
                if (event.colDef.colId == 'contextMenu')
                    return;
            },
            getRowId: ({ data: rowData }) => rowData.WallboardID,
            isExternalFilterPresent: () => true,
            doesExternalFilterPass: node => {
                let result = this.agGridHelperService.doesExternalFilterPass(node, this.searchValue)
                if (result) this.curWallboardCount++
                return result
            },
            onFilterChanged: () => {
                if (!this.isLoading) {
                    if (this.gridApi?.getDisplayedRowCount() === 0) {
                        this.gridApi?.showNoRowsOverlay();
                    }
                    else
                        this.gridApi?.hideOverlay();
                }
                this.gridApi?.redrawRows();
            },
            components: {
                CustomNoRowsOverlayComponent,
                contextMenuCellRenderer: CXOneWallboardContextMenuCellRendererComponent,
                cxoneTruncateTextCellRendererComponent: CxoneTruncateTextCellRendererComponent,
                agColumnHeader: CxoneColumnHeaderRendererComponent
            },
            noRowsOverlayComponent: 'CustomNoRowsOverlayComponent',
            noRowsOverlayComponentParams: {
                noRowsOverlayTextFunc: () => this.searchValue !== '' ?
                    this.getLocalizationString("No_Results_Found", "No results found")
                    : this.getLocalizationString("No_Wallboards_Added", "No wallboards added yet"),
                noRowsOverlayIconPathFunc: () => {
                    const path = this.searchValue !== '' ?
                        '../App_Themes/CXone/add-widget/wallboards-no-results.svg#wallboards-no-results'
                        : '../App_Themes/CXone/add-widget/wallboards-empty-state.svg#wallboards-empty-state';
                    return path;
                }
            },
            onGridSizeChanged:  params => {
                params.api.sizeColumnsToFit();
            },
            popupParent: document.querySelector('ag-grid-angular'), // Keeps the context menu within the bounds of the grid
            suppressRowClickSelection: true,
            suppressContextMenu: true,
            suppressCellFocus: true,
            suppressDragLeaveHidesColumns : true,
            rowClass: 'cx-one-ui',
            groupDefaultExpanded: 1,
            rowHeight: 36,
            headerHeight: 44,
            scrollbarWidth: 8,
            context: {
                getFilterText: () => this.searchValue,
                deleteWallboard:() => this.deleteWallboard(),
            },
        });
    }

    getLocalizationString(key: string, defaultValue: string, uppercase = false) {
        let returnString = this.cvLocalizationFactory.getCachedText("Wallboard_Management", key, defaultValue);

        if (uppercase)
            returnString = returnString.toUpperCase();

        return returnString;
    }
}
